// Client-side mirror of the backend's validation rules.
//
// The rules themselves live in orgMeta, per property, so there is ONE
// definition -- but it is enforced TWICE. This pass exists for the user's
// benefit: inline errors on blur, and a blocked submit instead of a round trip.
// It is never the authority. The backend re-validates everything
// (app/services/network_entity.py) and its per-field 400 response is rendered
// the same way as these errors, so if this file ever misses a rule the user
// still sees a correct message against the right field.
//
// Rule set and error codes: network-tab-spec.md sections 4.2 and 4.4.
// Keep both sides in step -- a ninth rule type is a deliberate change here and
// in the service.

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

// Applied BEFORE validation, matching the backend's order, so what the user
// sees on blur is exactly what will be stored and what uniqueness is checked
// against.
//
// Driven by the property's TYPE plus an optional `normalise` hint in config,
// not by hardcoded key names. Dispatching on keys would mean a newly declared
// `contactEmail` or `tinNumber` got no normalisation while the backend (which
// normalises by rule) still applied it -- so the two sides would validate
// different strings.
//
// `keyOrProperty` accepts either, so callers holding only a key still work.
export const normaliseValue = (keyOrProperty, value) => {
    if (value === null || value === undefined) return null;
    if (typeof value !== "string") return value;

    const property =
        typeof keyOrProperty === "string" ? { key: keyOrProperty } : keyOrProperty || {};
    const { key, type = "text", normalise } = property;

    let out = value.trim();

    if (type === "email") out = out.toLowerCase();
    else if (normalise === "upper") out = out.toUpperCase();
    else if (normalise === "lower") out = out.toLowerCase();

    // A display name should not carry double spaces whichever field it is.
    if (key === "name" || normalise === "collapse") {
        out = out.replace(/\s+/g, " ");
    }

    return out === "" ? null : out;
};

// One property -> the first error message, or "" when valid.
// First-error-only on purpose: MUI shows a single helperText per field, and the
// backend returns one code per field too.
export const validateProperty = (property, rawValue) => {
    const { label, type = "text", required } = property;
    const rules = property.validation || {};
    const value = normaliseValue(property, rawValue);

    if (value === null) {
        return required ? `${label} is required` : "";
    }

    if (["text", "textarea", "email", "phone"].includes(type)) {
        if (typeof value !== "string") return `${label} must be text`;
        if (rules.maxLength && value.length > rules.maxLength) {
            return `${label} must be at most ${rules.maxLength} characters`;
        }
        if (rules.minLength && value.length < rules.minLength) {
            return `${label} must be at least ${rules.minLength} characters`;
        }
    }

    if (type === "email" && !EMAIL_RE.test(value)) {
        return "Enter a valid email address";
    }

    if (type === "number") {
        const num = Number(value);
        if (Number.isNaN(num)) return `${label} must be a number`;
        if (rules.min !== undefined && num < rules.min) {
            return `${label} must be at least ${rules.min}`;
        }
        if (rules.max !== undefined && num > rules.max) {
            return `${label} must be at most ${rules.max}`;
        }
    }

    if (type === "select") {
        // Coerced: MUI TextField select hands back e.target.value as a
        // string, so a numeric config value would never match strictly.
        const allowed = (property.options || []).map((o) => String(o.value));
        if (!allowed.includes(String(value))) {
            return `${label} is not a valid choice`;
        }
    }

    if (rules.pattern && typeof value === "string") {
        // Built per call rather than cached: the pattern comes from config and
        // an invalid one must not take the form down. The backend rejects a bad
        // pattern at config load, so this is belt and braces.
        try {
            if (!new RegExp(rules.pattern).test(value)) {
                return rules.message || `${label} is not in the expected format`;
            }
        } catch {
            return "";
        }
    }

    return "";
};

// Every property of a type -> { [key]: message } for the failures only.
export const validateForm = (properties, values) => {
    const errors = {};
    (properties || []).forEach((property) => {
        const message = validateProperty(property, values[property.key]);
        if (message) errors[property.key] = message;
    });
    return errors;
};

// The API's data.errors -> the same { [key]: message } shape, so a server
// rejection lands on the form identically to a local one.
export const mapServerErrors = (response) => {
    const errors = {};
    (response?.data?.errors || []).forEach(({ field, message }) => {
        if (field) errors[field] = message;
    });
    return errors;
};

// Normalise every value before submit, so the payload matches what was
// validated and what the backend will store.
export const normaliseForm = (properties, values) => {
    const payload = {};
    (properties || []).forEach((property) => {
        payload[property.key] = normaliseValue(property, values[property.key]);
    });
    return payload;
};
